/**
 * The statistical report from casting lots (the CWC demo walk, 2026-09-26). Pure.
 *
 * A plant that books scrap per production day and part (an imported CMS) keeps its production record in casting lots.
 * Its heats carry only the castings of the lots they alone fed, so a report built on heats undercounts production and
 * scrap, and records no defect per heat, so its Pareto is empty. This builds the same ReportResult the renderers and
 * exporters already draw, from the lots booked in each period (GET /v1/twin/lots/rollup): a row is a period's lots,
 * produced is their castings, rejected their scrapped pieces, and the defects are pieces per defect as booked. It has
 * no grade or line breakdown and no failed units (a lot has no pass or fail of its own), and its parts come from the
 * rollup by part. Its shifts come from the rollup by shift, where the plant books its lots per shift (lotShiftRows);
 * a plant that books them per day has no shift split. reportUnitWords names the unit "lot" wherever a heat report
 * says "heat".
 */

import type { LotRollupRow } from "@/lib/api";
import {
  MAX_PART_ROWS,
  NO_SHIFT_LABEL,
  dayLabel,
  monthLabel,
  type DefectConcentration,
  type DefectCount,
  type GradeStatRow,
  type ReportGranularity,
  type ReportResult,
  type ReportStatRow,
  type ShiftStatRow,
} from "./statistics";

/** The rollup grouping a report granularity reads; a heat-wise report has none (it stays on heats). */
export function lotRollupBy(g: ReportGranularity): "day" | "week" | "month" | null {
  if (g === "daily") return "day";
  if (g === "weekly") return "week";
  if (g === "monthly") return "month";
  return null;
}

/** A period's row label, as a heat report labels the same bucket (the rollup's keys are the same keys). */
export function lotPeriodLabel(key: string, g: ReportGranularity): string {
  if (g === "daily") return dayLabel(key);
  if (g === "monthly") return monthLabel(key);
  return key;
}

/** The last plant day of the period that starts on `first` (YYYY-MM-DD), for the report's window when none was set. */
export function periodLastDay(first: string, g: ReportGranularity): string {
  const d = new Date(`${first}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return first;
  if (g === "monthly") d.setUTCMonth(d.getUTCMonth() + 1, 0);
  else if (g === "weekly") d.setUTCDate(d.getUTCDate() + 6);
  return d.toISOString().slice(0, 10);
}

/** A percentage to one decimal, or null without a denominator (the report's rule: never a 0 for "not counted"). */
function pct(part: number, whole: number): number | null {
  if (whole <= 0) return null;
  return Math.round((part / whole) * 1000) / 10;
}

/** Pieces per defect, most first, ties by name; zero or negative counts left out. */
function defectCounts(defects: Record<string, number>): DefectCount[] {
  return Object.entries(defects)
    .filter(([, c]) => Number.isFinite(c) && c > 0)
    .map(([defect, count]) => ({ defect, count }))
    .sort((a, b) => b.count - a.count || a.defect.localeCompare(b.defect));
}

/**
 * The defect concentration of the lots' pieces per defect (defectCounts): totalTagged is the scrapped pieces booked
 * with a defect, where a heat report counts the heats that recorded one.
 */
function lotDefectConcentration(topDefects: DefectCount[]): DefectConcentration {
  const taggedPieces = topDefects.reduce((s, d) => s + d.count, 0);
  const topDefect = topDefects[0] ?? null;
  return {
    distinctTypes: topDefects.length,
    totalTagged: taggedPieces,
    topDefect,
    topSharePct: topDefect ? pct(topDefect.count, taggedPieces) : null,
  };
}

/**
 * The shift table of a plant that books its lots per shift, from the lots rolled up by shift (GET
 * /v1/twin/lots/rollup?by=shift): a row per shift label, counting lots, their castings and scrapped pieces, with its
 * top defect; the lots booked with no shift in one row last (NO_SHIFT_LABEL). Nothing is derived: the plant booked each
 * lot's shift. None where no lot carries a shift: a plant that books its scrap per day has no shift split of it.
 */
export function lotShiftRows(shifts: readonly LotRollupRow[]): ShiftStatRow[] {
  if (!shifts.some((s) => s.key !== "" && s.lots > 0)) return [];
  return shifts
    .filter((s) => s.lots > 0)
    .map((s) => {
      const label = s.key === "" ? NO_SHIFT_LABEL : s.key;
      return {
        key: label,
        label,
        derived: false,
        heats: s.lots,
        produced: s.cast,
        accepted: s.cast - s.scrap,
        rejected: s.scrap,
        yieldPct: pct(s.cast - s.scrap, s.cast),
        rejectionPct: pct(s.scrap, s.cast),
        topDefect: defectCounts(s.defects)[0] ?? null,
      };
    })
    .sort((a, b) => {
      const none = (r: ShiftStatRow) => r.label === NO_SHIFT_LABEL;
      if (none(a) !== none(b)) return none(a) ? 1 : -1;
      return a.label.localeCompare(b.label, undefined, { numeric: true });
    });
}

/**
 * The heats a report window holds, as computeReport windows them: by the plant day of each heat's start where `dayOf`
 * names one (lib/shift-calendar plantDayOf), else inclusive UTC day bounds; a heat whose start does not parse kept (it
 * falls into an "unknown" bucket there).
 */
export function heatsInWindow<T extends { startedAt: string }>(
  heats: readonly T[],
  range: { from?: string | null; to?: string | null },
  dayOf?: (iso: string) => string | null,
): T[] {
  const fromMs = range.from ? Date.parse(`${range.from}T00:00:00Z`) : NaN;
  const toMs = range.to ? Date.parse(`${range.to}T23:59:59.999Z`) : NaN;
  return heats.filter((h) => {
    const day = dayOf?.(h.startedAt);
    if (day) return (!range.from || day >= range.from) && (!range.to || day <= range.to);
    const t = Date.parse(h.startedAt);
    if (Number.isNaN(t)) return true;
    if (!Number.isNaN(fromMs) && t < fromMs) return false;
    if (!Number.isNaN(toMs) && t > toMs) return false;
    return true;
  });
}

/**
 * Whether a period report counts casting lots: every heat in its window came from an import (or it has none), so the
 * plant's production record for the window is its lots. A heat run on the floor in the window keeps the heat report;
 * one elsewhere in the org's history does not decide this window (#962 review, M5).
 */
export function reportsFromLots(windowHeats: readonly { source?: string }[]): boolean {
  return windowHeats.every((h) => h.source === "import");
}

/**
 * A heat report of a plant that books its scrap per production day and part, with the plant's totals: its heats carry
 * only the castings of the lots they alone fed and no defect of their own, so its totals and top defects are the
 * lots booked in the window (the lot rollup's total), and lotTotals keeps what the heats' rows add up to. No heat
 * fails or passes on a lot's scrap: failedHeats is 0 and the page hides it. The AI report audit, 2026-09-27: such a
 * plant's heat report undercounted its castings by a fifth, named no top defect, and failed nearly every heat.
 *
 * Its defect concentration is the same lots' (as computeLotReport counts it): from the heats, which name no defect,
 * the page said "No defects recorded in this window" under a KPI naming the lots' top defect. Its shift split is the
 * lots' too, never the heats': the heats' shift sums were only the castings of the lots a heat alone fed. A plant that
 * books its lots per shift has one, from the lots rolled up by shift (`shifts`, lotShiftRows), counted in lots
 * (byShiftUnit); one that books them per day has none, and lotShiftNote says why the shift table is empty.
 */
export function withLotTotals(result: ReportResult, lots: LotRollupRow, shifts: readonly LotRollupRow[] = []): ReportResult {
  const topDefects = defectCounts(lots.defects);
  const byShift = lotShiftRows(shifts);
  return {
    ...result,
    totals: {
      ...result.totals,
      produced: lots.cast,
      accepted: lots.cast - lots.scrap,
      rejected: lots.scrap,
      yieldPct: pct(lots.cast - lots.scrap, lots.cast),
      rejectionPct: pct(lots.scrap, lots.cast),
      failedHeats: 0,
      topDefects,
    },
    defectConcentration: lotDefectConcentration(topDefects),
    byShift,
    byShiftUnit: byShift.length > 0 ? "lot" : undefined,
    lotTotals: { lots: lots.lots, heatsProduced: result.totals.produced },
  };
}

/** Why a heat report's rows add up to less than its totals, for a plant that books its scrap per lot; "" otherwise. */
export function lotTotalsNote(result: Pick<ReportResult, "lotTotals" | "totals">): string {
  const l = result.lotTotals;
  if (!l) return "";
  const n = (v: number) => v.toLocaleString("en-US");
  return (
    "This plant books its scrap per production day and part, in casting lots. A heat shows a lot's castings only when " +
    `it alone fed that lot, so the heats' rows add up to ${n(l.heatsProduced)} castings; the totals are the plant's ` +
    `${n(l.lots)} casting ${l.lots === 1 ? "lot" : "lots"}, ${n(result.totals.produced)} castings.`
  );
}

/**
 * Why a heat report of a plant that books its scrap per lot has no shift table (withLotTotals): its lots carry no
 * shift. "" otherwise, and "" where its lots carry shifts and the table is theirs.
 */
export function lotShiftNote(result: Pick<ReportResult, "lotTotals" | "byShift">): string {
  return result.lotTotals && result.byShift.length === 0
    ? "This plant books its scrap per production day on casting lots, so a shift split of its scrap is not available."
    : "";
}

/** How a part row is labelled when the lots booked no part. */
export const NO_PART_LABEL = "(no part)";

export function computeLotReport(
  periods: readonly LotRollupRow[],
  parts: readonly LotRollupRow[],
  granularity: ReportGranularity,
  dateRange?: { from?: string | null; to?: string | null },
  shifts: readonly LotRollupRow[] = [],
): ReportResult {
  // Oldest first, as a heat report orders its time buckets, so the report reads like a timeline (the rollup lists
  // periods newest first).
  const ordered = [...periods].sort(
    (a, b) => (a.first_day ?? a.key).localeCompare(b.first_day ?? b.key) || a.key.localeCompare(b.key),
  );
  const rows: ReportStatRow[] = ordered.map((p) => ({
    key: p.key,
    groupLabel: lotPeriodLabel(p.key, granularity),
    heats: p.lots,
    produced: p.cast,
    accepted: p.cast - p.scrap,
    rejected: p.scrap,
    yieldPct: pct(p.cast - p.scrap, p.cast),
    rejectionPct: pct(p.scrap, p.cast),
    failedHeats: 0,
    topDefects: defectCounts(p.defects),
  }));

  let lots = 0;
  let cast = 0;
  let scrap = 0;
  const defects: Record<string, number> = {};
  let firstDay: string | null = null;
  let lastDay: string | null = null;
  for (const p of periods) {
    lots += p.lots;
    cast += p.cast;
    scrap += p.scrap;
    for (const [d, c] of Object.entries(p.defects)) defects[d] = (defects[d] ?? 0) + c;
    const day = p.first_day ?? null;
    if (day && (firstDay === null || day < firstDay)) firstDay = day;
    const last = day ? periodLastDay(day, granularity) : null;
    if (last && (lastDay === null || last > lastDay)) lastDay = last;
  }
  const topDefects = defectCounts(defects);

  const allParts: GradeStatRow[] = parts.map((p) => ({
    key: p.key === "" ? NO_PART_LABEL : p.key,
    label: p.key === "" ? NO_PART_LABEL : p.key,
    heats: p.lots,
    produced: p.cast,
    accepted: p.cast - p.scrap,
    rejected: p.scrap,
    yieldPct: pct(p.cast - p.scrap, p.cast),
    rejectionPct: pct(p.scrap, p.cast),
  }));
  // The rollup ranks parts by scrap; kept, with the report's own tie-breaks, and capped as a heat report caps them.
  allParts.sort((a, b) => b.rejected - a.rejected || b.produced - a.produced || a.label.localeCompare(b.label));

  return {
    granularity,
    unit: "lot",
    rows,
    totals: {
      heats: lots,
      produced: cast,
      accepted: cast - scrap,
      rejected: scrap,
      yieldPct: pct(cast - scrap, cast),
      rejectionPct: pct(scrap, cast),
      failedHeats: 0,
      topDefects,
    },
    byShift: lotShiftRows(shifts),
    byGrade: [],
    byLine: [],
    byPart: allParts.slice(0, MAX_PART_ROWS),
    partsTotal: allParts.length,
    worstHeats: [],
    defectConcentration: lotDefectConcentration(topDefects),
    windowFrom: dateRange?.from
      ? new Date(`${dateRange.from}T00:00:00Z`).toISOString()
      : firstDay
        ? new Date(`${firstDay}T00:00:00Z`).toISOString()
        : null,
    windowTo: dateRange?.to
      ? new Date(`${dateRange.to}T23:59:59.999Z`).toISOString()
      : lastDay
        ? new Date(`${lastDay}T23:59:59.999Z`).toISOString()
        : null,
    generatedAt: new Date().toISOString(),
  };
}
